import { useCallback, useState } from 'react'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  DraftIssueInput,
  MutateIssueToReportInput,
  SubmitIssueInput,
  useCreateIssueFormItemsDataQuery,
  useDraftIssueMutation,
  useRemoveTempIssueNumberMutation,
  useSubmitIssueMutation,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import CreateIssueFormItems from './components/CreateIssueFormItems'
import {
  CreateIssueFormItemsProps,
  CreateIssueFormItemsValues,
} from './components/CreateIssueFormItems/types'
import ModalFooter from './components/ModalFooter'
import { ModalFooterProps } from './components/ModalFooter/types'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { CustomFieldEditorValues } from 'components/CustomFields/CustomFieldEditor/types'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import { SharePointParentFolder } from 'shared'
import { UseCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useIssueReportsSelectOptionsContext } from '../../IssueReportsSelectOptionsContext'
import { errorHandler } from 'hooks/errorHandler'

/**
 * a hook to handle common form items in the Issue and Identified Field Issue modules
 *
 * @see {@link CreateIssueFormItems}
 * @see {@link ModalFooter}
 * @see {@link CreateIssueFormItemsValues}
 */
export function useCreateIssueFormItems<T>({
  number,
  form,
  cleanup,
  toggle,
}: UseCreateIssueFormItemsOptions<T>) {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const { data, loading } = useCreateIssueFormItemsDataQuery({
    ...DefaultQueryHookOptions,
    skip: !projectGlobalId,
    variables: { projectId: projectGlobalId! },
  })
  const [submit] = useSubmitIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      submitIssue: {
        issue: { formattedNumber },
      },
    }) =>
      simpleSuccessNotification(`${formattedNumber} successfully submitted!`),
  })
  const [draft] = useDraftIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      draftIssue: {
        issue: { formattedNumber },
      },
    }) =>
      simpleSuccessNotification(
        `Successfully saved a draft for ${formattedNumber}!`
      ),
  })
  const [remove] = useRemoveTempIssueNumberMutation(DefaultMutationHookOptions)
  const types = useOptions({
    items: data?.issueTypes?.records,
    label: 'name',
    value: 'id',
    tooltip: 'description',
    sort: 'name',
  })
  const [mutating, setMutating] = useState(false)
  const reports = useIssueReportsSelectOptionsContext()
  const payItems = usePayItemSelectContext()
  const { toMutateCustomFieldsInput } = useProjectCustomFieldsContext()
  const cleanUp = useCallback(() => {
    if (!projectGlobalId) return errorHandler('projectId is not defined')
    if (cleanup)
      remove({
        variables: {
          input: {
            projectId: projectGlobalId,
            byFolder: {
              folder: number,
              parentId: SharePointParentFolder.Issues.id,
            },
          },
        },
      })
    toggle(null)
  }, [cleanup, number, projectGlobalId, remove, toggle])
  const draftSubmitMeta = useCallback(
    ({
      customFields,
      issueTypes,
      externalLinks,
      identifiedFieldIssueId,
      id,
      number,
      ...options
    }: DraftSubmitOptions) => {
      return {
        id,
        number,
        identifiedFieldIssueId,
        customFields: toMutateCustomFieldsInput(customFields),
        typeIds: issueTypes.map(({ value }) => value.toString()),
        externalLinks: externalLinks?.map(({ href, label }) => ({
          label,
          href,
        })),
        payItemIds: payItems.fromLabeledValues(options.payItems),
        reports: reports
          .fromLabeledValues(options.reports)
          .reduce((reports, value) => {
            if (value.reportId !== identifiedFieldIssueId) reports.push(value)
            return reports
          }, Array<MutateIssueToReportInput>()),
      }
    },
    [payItems, reports, toMutateCustomFieldsInput]
  )
  return {
    CreateIssueFormItems: (
      <CreateIssueFormItems types={types} loading={loading} number={number} />
    ),
    ModalFooter: (
      <ModalFooter mutating={mutating} form={form} onClose={() => cleanUp()} />
    ),
    mutating,
    setMutating,
    /**
     * sets {@link mutating} for you
     */
    draftSubmit: async (options: DraftSubmitOptions) => {
      setMutating(true)
      const projectId = options.id ? undefined : projectGlobalId
      if (options.submit) {
        const { description } = options
        await submit({
          variables: {
            input: { ...draftSubmitMeta(options), projectId, description },
          },
        })
      } else {
        const { description } = options
        await draft({
          variables: {
            input: { ...draftSubmitMeta(options), projectId, description },
          },
        })
      }
      setMutating(false)
    },
    /**
     * invokes {@link toggle} for you and should only be used for cancel buttons
     */
    cleanUp,
  }
}

type DraftSubmitOptions = CreateIssueFormItemsValues &
  Partial<CustomFieldEditorValues> &
  Pick<
    SubmitIssueInput | DraftIssueInput,
    'id' | 'identifiedFieldIssueId' | 'number'
  >

type UseCreateIssueFormItemsOptions<T> = Pick<
  CreateIssueFormItemsProps,
  'number'
> &
  Pick<ModalFooterProps<T>, 'form'> & { cleanup: boolean } & Pick<
    UseCIMSModalValues<any>,
    'toggle'
  >
